// src/components/features/integrations/IntegrationCard.jsx
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Settings2, Lock, Loader2, Plus } from 'lucide-react';
import { PixelService } from '@/services/pixel.service';
import { IntegrationService } from '@/services/integration.service';
import { queryClient } from '@/lib/react-query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useState } from 'react';

export function IntegrationCard({
  integration,
  onSetup,
  connectedPixels = [],
}) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [selectedPixel, setSelectedPixel] = useState(null);

  // Fetch all connected pixels details
  const { data: pixelsData, isLoading } = useQuery({
    queryKey: ['pixels', { ids: connectedPixels }],
    queryFn: () => PixelService.getPixels({ connectedPixels }),
    enabled: connectedPixels.length > 0,
  });

  // Find pixels with and without this integration
  const connectedPixelsWithIntegration =
    pixelsData?.pixels?.filter((pixel) =>
      pixel.integrations?.some((i) => i.name === integration.id),
    ) || [];

  const availablePixels =
    pixelsData?.pixels?.filter(
      (pixel) => !pixel.integrations?.some((i) => i.name === integration.id),
    ) || [];

  const connectedCount = connectedPixelsWithIntegration.length;

  const handleDelete = async () => {
    if (!selectedPixel) return;

    setIsDeleting(true);
    try {
      switch (integration.id) {
        case 'activeCampaign':
          await IntegrationService.removeActiveCampaign(selectedPixel);
          break;
        // Add other integration cases here as needed
      }
      setShowDeleteAlert(false);
      setSelectedPixel(null);
      // Refresh pixels data
      await queryClient.invalidateQueries(['pixels']);
    } catch (error) {
      console.error('Error removing integration:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleIntegrationClick = (pixel = null) => {
    if (!integration.isAvailable) return;
    onSetup(integration);
  };

  const handleManageClick = (pixel) => {
    setSelectedPixel(pixel.id);
    setShowDeleteAlert(true);
  };

  const renderConnectedPixels = () => {
    if (connectedPixelsWithIntegration.length === 0) return null;

    return (
      <div className="sl-connected-pixels space-y-3 mt-4">
        <h4 className="sl-connected-title text-sm font-medium text-gray-700">
          Connected Pixels
        </h4>
        <div className="sl-pixel-list space-y-2">
          {connectedPixelsWithIntegration.map((pixel) => (
            <div
              key={pixel.id}
              className="sl-pixel-item flex items-center justify-between rounded-lg border border-gray-200 p-3">
              <div className="sl-pixel-info">
                <code className="text-xs font-mono text-purple-600 bg-purple-50 px-2 py-1 rounded">
                  {pixel.id}
                </code>
              </div>
              <div className="sl-pixel-actions flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  className="text-xs"
                  onClick={() => handleManageClick(pixel)}>
                  <Settings2 className="h-3 w-3 mr-1" />
                  Manage
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderAvailablePixels = () => {
    if (availablePixels.length === 0) return null;

    return (
      <div className="sl-available-pixels space-y-3 mt-4">
        <h4 className="sl-available-title text-sm font-medium text-gray-700">
          Available Pixels
        </h4>
        <div className="sl-pixel-list space-y-2">
          {availablePixels.map((pixel) => (
            <div
              key={pixel.id}
              className="sl-pixel-item flex items-center justify-between rounded-lg border border-gray-200 p-3">
              <div className="sl-pixel-info">
                <code className="text-xs font-mono text-purple-600 bg-purple-50 px-2 py-1 rounded">
                  {pixel.id}
                </code>
              </div>
              <div className="sl-pixel-actions flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  className="text-xs"
                  onClick={() => handleIntegrationClick()}>
                  <Plus className="h-3 w-3 mr-1" />
                  Add Integration
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className={`sl-integration-card group relative rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-all duration-200 ${
          !integration.isAvailable && 'cursor-not-allowed'
        }`}>
        {/* Blur overlay for unavailable integrations */}
        {!integration.isAvailable && (
          <div className="sl-integration-overlay absolute inset-0 z-10 rounded-lg backdrop-blur-[2px] bg-white/50 flex items-center justify-center">
            <div className="sl-coming-soon-badge flex items-center gap-2 rounded-full bg-gray-900/80 px-4 py-2 text-sm font-medium text-white">
              <Lock className="h-4 w-4" />
              Coming Soon
            </div>
          </div>
        )}

        <div className="sl-integration-content space-y-6">
          <div className="sl-integration-header flex items-start justify-between">
            <div className="sl-integration-logo-container h-12 w-12 rounded-lg border border-gray-100 bg-white p-2">
              <img
                src={integration.logo}
                alt={`${integration.name} logo`}
                className="sl-integration-logo h-full w-full object-contain"
              />
            </div>
            {connectedCount > 0 && (
              <Badge variant="success" className="sl-connection-badge">
                Connected with {connectedCount} Pixel
                {connectedCount !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>

          <div className="sl-integration-info space-y-2">
            <h3 className="sl-integration-name text-lg font-semibold text-gray-900">
              {integration.name}
            </h3>
            <p className="sl-integration-description text-sm text-gray-600">
              {integration.description}
            </p>
          </div>

          {renderConnectedPixels()}
          {renderAvailablePixels()}
        </div>
      </div>

      <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
        <AlertDialogContent className="sl-alert-dialog-content">
          <AlertDialogHeader className="sl-alert-dialog-header">
            <AlertDialogTitle className="sl-alert-dialog-title">
              Update Integration
            </AlertDialogTitle>
            <AlertDialogDescription className="sl-alert-dialog-description">
              To update your {integration.name} credentials, the current
              integration needs to be removed first. After removal, you'll be
              able to set up the integration with new credentials. Would you
              like to proceed with removing the current integration?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sl-alert-dialog-footer">
            <AlertDialogCancel className="sl-alert-dialog-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="sl-alert-dialog-action bg-red-600 hover:bg-red-700"
              disabled={isDeleting}>
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Removing...
                </>
              ) : (
                'Remove & Update'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
